import { useEffect, useId, useRef, useState } from 'react'
import type {
  ButtonHTMLAttributes,
  ComponentType,
  ReactNode,
  SelectHTMLAttributes,
} from 'react'
import { IconChevronDown, IconChevronRight, IconHelp, IconRevenue, IconTrendDown, IconX, type ConsoleIconProps } from './icons'

/**
 * The console kit — the adult tiers' building blocks, one per component on
 * the Figma "Components" page (file "Kalqy"). Sizes are the Figma ones; colour
 * comes only from the console tokens (`.console` in console.css), so nothing
 * here names a hex value.
 *
 * ⚠️ PRESENTATION ONLY. None of these fetches, formats a domain value, or
 * decides what a number means — a screen passes in a string it has already
 * built from real data, and passes `null` (rendered as "—") when it has none.
 */

type IconComponent = ComponentType<ConsoleIconProps>

function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ')
}

/* ── Button ──────────────────────────────────────────────────────────────── */

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'destructive'
export type ButtonSize = 'md' | 'sm'

const BUTTON_VARIANT: Record<ButtonVariant, string> = {
  primary:
    'border-primary bg-primary text-primary-ink shadow-[0_1px_1px_rgba(0,0,0,0.06)] hover:bg-primary-deep',
  secondary:
    'border-line-strong bg-surface text-ink shadow-[0_1px_1px_rgba(0,0,0,0.06)] hover:bg-surface-subtle',
  ghost: 'border-transparent bg-transparent text-ink hover:bg-surface-muted',
  destructive:
    'border-danger bg-danger text-white shadow-[0_1px_1px_rgba(0,0,0,0.06)] hover:opacity-90',
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  icon?: IconComponent
  /** Icon after the label (e.g. an arrow on "Open requests"). */
  trailingIcon?: IconComponent
}

export function Button({
  variant = 'secondary',
  size = 'md',
  icon: Icon,
  trailingIcon: Trailing,
  className,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cx(
        'inline-flex shrink-0 items-center justify-center gap-1.5 rounded-md border font-dash font-medium whitespace-nowrap transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-50',
        size === 'md' ? 'h-8 px-3 text-[13px] leading-[18px]' : 'h-7 px-2.5 text-[12.5px] leading-4',
        BUTTON_VARIANT[variant],
        className,
      )}
      {...rest}
    >
      {Icon ? <Icon size={size === 'md' ? 16 : 14} /> : null}
      {children}
      {Trailing ? <Trailing size={size === 'md' ? 16 : 14} /> : null}
    </button>
  )
}

/* ── Badge ───────────────────────────────────────────────────────────────── */

export type Tone = 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'brand'

const BADGE_TONE: Record<Tone, string> = {
  success: 'border-pill-success-line bg-pill-success-bg text-pill-success-fg',
  warning: 'border-pill-warning-line bg-pill-warning-bg text-pill-warning-fg',
  danger: 'border-pill-danger-line bg-pill-danger-bg text-pill-danger-fg',
  info: 'border-pill-info-line bg-pill-info-bg text-pill-info-fg',
  neutral: 'border-pill-neutral-line bg-pill-neutral-bg text-pill-neutral-fg',
  brand: 'border-primary bg-primary text-primary-ink',
}

export function Badge({
  tone = 'neutral',
  dot = false,
  children,
}: {
  tone?: Tone
  dot?: boolean
  children: ReactNode
}) {
  return (
    <span
      className={cx(
        'inline-flex shrink-0 items-center gap-1.5 rounded-sm border px-1.5 font-dash text-[11.5px] leading-4 font-medium whitespace-nowrap',
        BADGE_TONE[tone],
      )}
    >
      {dot ? <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-current" /> : null}
      {children}
    </span>
  )
}

/* ── Card ────────────────────────────────────────────────────────────────── */

export function Card({
  children,
  className,
  as: Tag = 'section',
  ...rest
}: {
  children: ReactNode
  className?: string
  as?: 'section' | 'div' | 'article'
  'aria-label'?: string
  'aria-labelledby'?: string
}) {
  return (
    <Tag className={cx('rounded-lg border border-line bg-surface shadow-card', className)} {...rest}>
      {children}
    </Tag>
  )
}

export function CardHeader({
  title,
  subtitle,
  actions,
  id,
  className,
}: {
  title: ReactNode
  subtitle?: ReactNode
  actions?: ReactNode
  id?: string
  className?: string
}) {
  return (
    <div className={cx('flex items-start justify-between gap-3 px-5 pt-4 pb-3', className)}>
      <div className="min-w-0">
        <h2 id={id} className="font-dash text-[14px] leading-5 font-semibold text-ink">
          {title}
        </h2>
        {subtitle ? <p className="mt-0.5 font-dash text-[12px] leading-4 text-muted">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  )
}

/* ── Page header ─────────────────────────────────────────────────────────── */

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: ReactNode
  subtitle?: ReactNode
  actions?: ReactNode
}) {
  return (
    <header className="mb-5 flex flex-wrap items-end justify-between gap-x-4 gap-y-3">
      <div className="min-w-0">
        <h1 className="font-dash text-[22px] leading-7 font-semibold tracking-[-0.02em] text-ink">{title}</h1>
        {subtitle ? <p className="mt-1 font-dash text-[13px] leading-5 text-muted">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </header>
  )
}

/* ── Icon tile ───────────────────────────────────────────────────────────── */

const TILE_TONE: Record<Tone | 'muted', string> = {
  muted: 'bg-surface-muted text-ink-soft',
  success: 'bg-pill-success-bg text-pill-success-fg',
  warning: 'bg-pill-warning-bg text-pill-warning-fg',
  danger: 'bg-pill-danger-bg text-pill-danger-fg',
  info: 'bg-pill-info-bg text-pill-info-fg',
  neutral: 'bg-surface-muted text-ink-soft',
  brand: 'bg-primary text-primary-ink',
}

export function IconTile({
  icon: Icon,
  tone = 'muted',
  size = 28,
}: {
  icon: IconComponent
  tone?: Tone | 'muted'
  size?: 28 | 36
}) {
  return (
    <span
      aria-hidden="true"
      className={cx(
        'inline-flex shrink-0 items-center justify-center',
        size === 28 ? 'h-7 w-7 rounded-[8px]' : 'h-9 w-9 rounded-[9px]',
        TILE_TONE[tone],
      )}
    >
      <Icon size={size === 28 ? 18 : 18} />
    </span>
  )
}

/* ── Stat card ───────────────────────────────────────────────────────────── */

export type Trend = 'up' | 'down' | 'neutral'

export interface StatCardProps {
  label: ReactNode
  /** Already formatted. `null` renders as an em dash — never a made-up zero. */
  value: string | null
  icon?: IconComponent
  /** The line under the figure ("+412 vs previous 30 days"). */
  delta?: ReactNode
  trend?: Trend
  loading?: boolean
  /** Where the number comes from. With it, the card opens a panel that says so. */
  source?: StatSource
}

export function StatCard({ label, value, icon, delta, trend = 'neutral', loading = false, source }: StatCardProps) {
  const TrendIcon = trend === 'down' ? IconTrendDown : IconRevenue
  const [open, setOpen] = useState(false)
  const body = (
    <>
      <div className="flex items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-1 font-dash text-[12.5px] leading-[18px] font-medium text-ink-soft">
          <span className="truncate">{label}</span>
          {source ? <IconHelp size={13} className="shrink-0 text-muted" aria-hidden="true" /> : null}
        </span>
        {icon ? <IconTile icon={icon} /> : null}
      </div>
      {loading ? (
        <Skeleton className="h-[34px] w-24" />
      ) : (
        <span className="tabular block font-dash text-[28px] leading-[34px] font-semibold tracking-[-0.02em] text-ink">
          {value ?? '—'}
        </span>
      )}
      {delta ? (
        <span
          className={cx(
            'flex min-w-0 items-center gap-1 font-dash text-[11.5px] leading-4 font-medium',
            trend === 'up' ? 'text-success' : trend === 'down' ? 'text-danger' : 'text-muted',
          )}
        >
          {trend !== 'neutral' ? <TrendIcon size={14} className="shrink-0" /> : null}
          <span className="truncate">{delta}</span>
        </span>
      ) : null}
    </>
  )
  const frame = 'flex min-w-0 flex-col gap-1.5 rounded-lg border border-line bg-surface px-[18px] py-4 shadow-card'
  if (!source) return <div className={frame}>{body}</div>
  return (
    <>
      <button
        type="button"
        aria-haspopup="dialog"
        title="Where this number comes from"
        onClick={() => {
          setOpen(true)
        }}
        className={cx(frame, 'w-full text-left transition-colors hover:border-line-strong focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink')}
      >
        {body}
      </button>
      <SourceDialog
        open={open}
        label={label}
        value={loading ? null : value}
        source={source}
        onClose={() => {
          setOpen(false)
        }}
      />
    </>
  )
}

/* ── Where a number comes from ───────────────────────────────────────────── */

/**
 * What a figure on a dashboard counts, written for the person reading it.
 *
 * ⚠️ WORDS, NOT TABLE NAMES. A head teacher reads "Play sessions started in
 * this period", never `Session.startedAt`. Every field is a sentence or a
 * short phrase the screen has already filled in with real dates.
 */
export interface StatSource {
  /** What is counted, and what is not ("Children with at least one play session. A child counts once."). */
  what: string
  /** Which days, when the figure has a period ("17 Sep – 16 Oct"). Omit for live or all-time figures. */
  period?: string
  /** Where on Kalqy the records live ("Play sessions", "Answers given in games"). */
  from: string
  /** The list behind the number, where the platform has one. */
  open?: { label: string; onOpen: () => void }
}

/**
 * The panel a figure opens: what it counts, over which days, where from, and
 * a way to the records. A native `<dialog>` — focus is trapped, Escape closes —
 * as a bottom sheet on a phone and a centred card from `sm` up.
 */
export function SourceDialog({
  open,
  label,
  value,
  source,
  onClose,
}: {
  open: boolean
  label: ReactNode
  value: ReactNode
  source: StatSource
  onClose: () => void
}) {
  const ref = useRef<HTMLDialogElement>(null)
  const titleId = useId()
  useEffect(() => {
    const d = ref.current
    if (!d) return
    if (open && !d.open) d.showModal()
    if (!open && d.open) d.close()
  }, [open])
  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onCancel={(e) => {
        e.preventDefault()
        onClose()
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
      className="console mx-0 mt-auto mb-0 w-full max-w-none rounded-t-lg border border-line bg-surface p-0 text-left text-ink shadow-raised backdrop:bg-ink/30 sm:m-auto sm:w-[min(92vw,28rem)] sm:rounded-lg"
    >
      <div className="p-5 pb-[calc(1.25rem+var(--safe-b,0px))] sm:pb-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-dash text-[12px] leading-4 font-medium text-muted">Where this number comes from</p>
            <h2 id={titleId} className="mt-1 font-dash text-[15px] leading-5 font-semibold text-ink">
              {label}
              {value !== null && value !== undefined ? <span className="tabular text-ink-soft"> · {value}</span> : null}
            </h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="-mt-1 -mr-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted hover:bg-surface-muted hover:text-ink">
            <IconX size={16} />
          </button>
        </div>
        <p className="mt-3 font-dash text-[13px] leading-5 text-ink-soft">{source.what}</p>
        <dl className="mt-4 grid grid-cols-[6.5rem_minmax(0,1fr)] gap-x-3 gap-y-2 border-t border-line pt-3 font-dash text-[12.5px] leading-5">
          {source.period ? (
            <>
              <dt className="text-muted">Period</dt>
              <dd className="text-ink">{source.period}</dd>
            </>
          ) : null}
          <dt className="text-muted">Counted from</dt>
          <dd className="text-ink">{source.from}</dd>
        </dl>
        {source.open ? (
          <div className="mt-5 flex justify-end">
            <Button
              trailingIcon={IconChevronRight}
              className="w-full sm:w-auto"
              onClick={() => {
                onClose()
                source.open?.onOpen()
              }}
            >
              {source.open.label}
            </Button>
          </div>
        ) : null}
      </div>
    </dialog>
  )
}

/* ── Meter ───────────────────────────────────────────────────────────────── */

/** A plain proportion bar. `value` is 0–1; out-of-range is clamped. */
export function Meter({
  value,
  label,
  height = 6,
}: {
  value: number
  /** Accessible label, e.g. "Karnataka, 14 schools". */
  label?: string
  height?: 4 | 6 | 8
}) {
  const pct = Math.max(0, Math.min(1, Number.isFinite(value) ? value : 0)) * 100
  return (
    <div
      role="meter"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pct)}
      className="w-full overflow-hidden rounded-full bg-surface-muted"
      style={{ height }}
    >
      <div className="h-full rounded-full bg-primary" style={{ width: `${String(pct)}%` }} />
    </div>
  )
}

/* ── Attention row ───────────────────────────────────────────────────────── */

export function AttentionRow({
  icon,
  tone = 'muted',
  title,
  meta,
  badge,
  action,
}: {
  icon: IconComponent
  tone?: Tone | 'muted'
  title: ReactNode
  meta?: ReactNode
  badge?: ReactNode
  action?: ReactNode
}) {
  return (
    <li className="flex items-center gap-3.5 border-t border-line px-5 py-3.5">
      <IconTile icon={icon} tone={tone} size={36} />
      <div className="min-w-0 flex-1">
        <p className="truncate font-dash text-[13px] leading-5 font-medium text-ink">{title}</p>
        {meta ? <p className="truncate font-dash text-[12px] leading-4 text-muted">{meta}</p> : null}
      </div>
      {badge}
      {action}
    </li>
  )
}

/* ── Select chip ─────────────────────────────────────────────────────────── */

/** A native select drawn as the console's filter chip ("State: All India ⌄"). */
export function SelectChip({
  label,
  className,
  children,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & { label?: string }) {
  return (
    <label
      className={cx(
        'relative inline-flex h-8 shrink-0 items-center rounded-[8px] border border-line bg-surface pr-7 pl-2.5 font-dash text-[13px] text-ink shadow-[0_1px_1px_rgba(0,0,0,0.06)] focus-within:border-line-strong',
        className,
      )}
    >
      {label ? <span className="mr-1 whitespace-nowrap">{label}:</span> : null}
      <select
        className="cursor-pointer appearance-none bg-transparent pr-1 font-medium outline-none"
        {...rest}
      >
        {children}
      </select>
      <IconChevronDown size={14} className="pointer-events-none absolute right-2 text-muted" />
    </label>
  )
}

/* ── Skeleton / empty ────────────────────────────────────────────────────── */

export function Skeleton({ className }: { className?: string }) {
  return <span aria-hidden="true" className={cx('block animate-pulse rounded-sm bg-surface-muted', className)} />
}

export function EmptyState({
  icon,
  title,
  body,
  action,
}: {
  icon?: IconComponent
  title: ReactNode
  body?: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center px-6 py-10 text-center">
      {icon ? <IconTile icon={icon} size={36} /> : null}
      <p className="mt-3 font-dash text-[13px] leading-5 font-medium text-ink">{title}</p>
      {body ? <p className="mt-1 max-w-[46ch] font-dash text-[12px] leading-4 text-muted">{body}</p> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  )
}

/** A short inline error with a retry, for one card whose request failed. */
export function CardError({ message, onRetry }: { message: ReactNode; onRetry?: () => void }) {
  return (
    <div role="alert" className="flex items-center justify-between gap-3 px-5 py-4">
      <p className="font-dash text-[12.5px] text-danger">{message}</p>
      {onRetry ? (
        <Button size="sm" onClick={onRetry}>
          Try again
        </Button>
      ) : null}
    </div>
  )
}
